import React, { type ComponentPropsWithRef, type ElementRef, useRef } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Icon from '@atlaskit/icon';
import PresenceBusyIcon from '@atlaskit/icon/glyph/presence-busy';
import { xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import {
	Radio,
	Button,
	TextField,
	colors,
	Tooltip,
	Field,
	type CustomDateRange,
} from '@atlassian/jira-portfolio-3-common';
import { DelayedAnnouncer } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/common/delayed-announcer';
import CustomTimelineRange from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/title-bar/share-report/components/custom-timeline-range';
import resizeIcon from '@atlassian/jira-portfolio-3-portfolio/src/common/icons/resize.raw.svg';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';

import type { ExportPngTimelineWidthOptions } from '../types';
import { EXPORT_PNG_TIMELINE_WIDTH_OPTIONS } from '../util';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';

type Props = {
	isDisabled: boolean;
	selection: ExportPngTimelineWidthOptions;
	setSelection: (type: ExportPngTimelineWidthOptions, analyticsEvent: UIAnalyticsEvent) => void;
	customWidth: string;
	setCustomWidth: (width: string) => void;
	customDateRange: CustomDateRange;
	setCustomDateRange: (range: CustomDateRange) => void;
};

const ExportTimelineSettings = ({
	isDisabled,
	selection,
	setSelection,
	customWidth,
	setCustomWidth,
	customDateRange,
	setCustomDateRange,
}: Props) => {
	const { formatMessage } = useIntl();

	const announcerRef = useRef<ElementRef<typeof DelayedAnnouncer>>(null);

	const createSelectionSetter =
		(type: ExportPngTimelineWidthOptions): ComponentPropsWithRef<typeof Radio>['onChange'] =>
		(_, analyticsEvent) =>
			setSelection(type, analyticsEvent);

	const createSelectionSetterOld =
		(type: ExportPngTimelineWidthOptions): ComponentPropsWithRef<typeof Button>['onClick'] =>
		(_, analyticsEvent) =>
			setSelection(type, analyticsEvent);

	const timelineSettingsContainer = xcss({
		display: 'flex',
		flexDirection: 'column',
		flexWrap: 'wrap',
		gap: 'space.100',
	});

	const timelineSettingsField = xcss({
		marginTop: 'space.negative.075',
		gap: 'space.025',
	});

	const timelineWidthContainer = xcss({
		display: 'flex',
		alignItems: 'center',
		gap: 'space.100',
	});

	const customTextBoxContainer = xcss({
		alignItems: 'center',
		caretColor: 'color.icon.accent.gray.subtle',
		display: 'flex',
		marginLeft: 'space.100',
	});

	const customTextBoxContainerLabel = xcss({
		marginLeft: 'space.050',
		color: 'color.text.subtle',
		fontSize: '16px',
	});

	return (
		<>
			{ff('jsw.roadmaps.plan-timeline-screen-missing-list-mark-up_7zqp1') ? (
				<Box xcss={timelineSettingsContainer}>
					<Box xcss={timelineSettingsField}>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<fieldset className={styles.timelineSettingsFieldset}>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<legend className={styles.timelineSettingsLegend}>
								{formatMessage(messages.widthLabel)}
							</legend>
							<Box xcss={timelineWidthContainer}>
								<Radio
									testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.share-report.components.png-export.timeline-settings.small-timeline"
									isDisabled={isDisabled}
									isChecked={selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.SMALL}
									onChange={createSelectionSetter(EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.SMALL)}
									label={formatMessage(messages.smallLabel)}
								/>
								<Radio
									testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.share-report.components.png-export.timeline-settings.medium-timeline"
									isDisabled={isDisabled}
									isChecked={selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.MEDIUM}
									onChange={createSelectionSetter(EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.MEDIUM)}
									label={formatMessage(messages.mediumLabel)}
								/>
								<Radio
									testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.share-report.components.png-export.timeline-settings.large-timeline"
									isDisabled={isDisabled}
									isChecked={selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.LARGE}
									onChange={createSelectionSetter(EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.LARGE)}
									label={formatMessage(messages.largeLabel)}
								/>
								<Radio
									testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.share-report.components.png-export.timeline-settings.custom-timeline"
									isDisabled={isDisabled}
									isChecked={selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.CUSTOM}
									onChange={createSelectionSetter(EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.CUSTOM)}
									label={formatMessage(messages.customLabel)}
								/>
								<Box xcss={customTextBoxContainer}>
									<TextField
										testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.share-report.components.png-export.timeline-settings.custom-timeline-width"
										isCompact
										isDisabled={
											selection !== EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.CUSTOM || isDisabled
										}
										type="number"
										value={customWidth}
										min={400}
										max={5000}
										onChange={(e) => {
											setCustomWidth(e.target.value);
											announcerRef.current?.announce();
										}}
										width="80"
										aria-label={formatMessage(messages.customWidthPixelLabelText)}
									/>
									<DelayedAnnouncer ref={announcerRef} label={customWidth} />
									<Box xcss={customTextBoxContainerLabel}>
										{formatMessage(messages.customWidthPixelLabel)}
									</Box>
								</Box>
							</Box>
						</fieldset>
					</Box>
					<Box xcss={timelineSettingsField}>
						<Field
							label={formatMessage(messages.timelineRangeLabel)}
							name="timelineRange"
							defaultValue=""
						>
							{({ fieldProps }) => (
								<CustomTimelineRange
									fieldProps={fieldProps}
									customDateRange={customDateRange}
									onCustomDatesChange={setCustomDateRange}
									isDisabled={selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.NONE || isDisabled}
								/>
							)}
						</Field>
					</Box>
				</Box>
			) : (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				<div className={styles.timelineSettingsContainerOld}>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={styles.timelineSettingsField}>
						<Field label={formatMessage(messages.widthLabel)} name="timelineWidth" defaultValue="">
							{() => (
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								<div className={styles.timelineWidthContainerOld}>
									<Tooltip content={formatMessage(messages.hideTimelineTooltip)}>
										<Button
											testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.share-report.components.png-export.timeline-settings.no-timeline"
											isDisabled={isDisabled}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
											className={`${styles.iconButton} ${styles.leftButton}`}
											isSelected={selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.NONE}
											iconAfter={
												<PresenceBusyIcon
													label={formatMessage(messages.hideTimelineTooltip)}
													secondaryColor={
														selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.NONE
															? token('color.icon.inverse', colors.N500)
															: token('color.icon.inverse', colors.N0)
													}
												/>
											}
											onClick={createSelectionSetterOld(EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.NONE)}
										/>
									</Tooltip>
									<Tooltip content={formatMessage(messages.smallTimelineTooltip)}>
										<Button
											testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.share-report.components.png-export.timeline-settings.small-timeline"
											isDisabled={isDisabled}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
											className={`${styles.iconButton} ${styles.smallIcon}`}
											iconAfter={<Icon label="" dangerouslySetGlyph={resizeIcon} />}
											isSelected={selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.SMALL}
											onClick={createSelectionSetterOld(EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.SMALL)}
										/>
									</Tooltip>
									<Tooltip content={formatMessage(messages.mediumTimelineTooltip)}>
										<Button
											testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.share-report.components.png-export.timeline-settings.medium-timeline"
											isDisabled={isDisabled}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
											className={`${styles.iconButton} ${styles.mediumIcon}`}
											iconAfter={<Icon label="" dangerouslySetGlyph={resizeIcon} />}
											isSelected={selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.MEDIUM}
											onClick={createSelectionSetterOld(EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.MEDIUM)}
										/>
									</Tooltip>
									<Tooltip content={formatMessage(messages.largeTimelineTooltip)}>
										<Button
											testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.share-report.components.png-export.timeline-settings.large-timeline"
											isDisabled={isDisabled}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
											className={`${styles.iconButton} ${styles.largeIcon}`}
											iconAfter={<Icon label="" dangerouslySetGlyph={resizeIcon} />}
											isSelected={selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.LARGE}
											onClick={createSelectionSetterOld(EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.LARGE)}
										/>
									</Tooltip>
									<Button
										testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.share-report.components.png-export.timeline-settings.custom-timeline"
										isDisabled={isDisabled}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										className={`${styles.iconButton} ${styles.rightButton}`}
										isSelected={selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.CUSTOM}
										onClick={createSelectionSetterOld(EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.CUSTOM)}
									>
										{formatMessage(messages.customLabel)}
									</Button>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<div className={styles.customTextBoxContainer}>
										<TextField
											testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.share-report.components.png-export.timeline-settings.custom-timeline-width"
											isCompact
											isDisabled={
												selection !== EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.CUSTOM || isDisabled
											}
											type="number"
											value={customWidth}
											min={400}
											max={5000}
											onChange={(e) => {
												setCustomWidth(e.target.value);
											}}
											width="80"
											aria-label={formatMessage(messages.customWidthPixelLabelText)}
										/>
										{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
										<div className={styles.customTextBoxContainerLabel}>
											{formatMessage(messages.customWidthPixelLabel)}
										</div>
									</div>
								</div>
							)}
						</Field>
					</div>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={styles.timelineSettingsField}>
						<Field
							label={formatMessage(commonMessages.dateRange)}
							name="timelineRange"
							defaultValue=""
						>
							{({ fieldProps }) => (
								<CustomTimelineRange
									fieldProps={fieldProps}
									customDateRange={customDateRange}
									onCustomDatesChange={setCustomDateRange}
									isDisabled={selection === EXPORT_PNG_TIMELINE_WIDTH_OPTIONS.NONE || isDisabled}
								/>
							)}
						</Field>
					</div>
				</div>
			)}
		</>
	);
};

export default ExportTimelineSettings;
